import {
  Button,
  Checkbox,
  Drawer,
  Empty,
  Input,
  notification,
  Spin,
  Upload,
} from "antd";
import { get } from "lodash";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineUploadFile } from "react-icons/md";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";

const TRANSFER_KIND = "kayros.insurance-plans";
const TRANSFER_VERSION = 1;

const planLabel = (plan) => plan.name || plan.identifier || plan.id;

const matchesSearch = (plan, search) => {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  const name = (plan.name || "").toLowerCase();
  const identifier = (plan.identifier || "").toLowerCase();
  return name.indexOf(needle) !== -1 || identifier.indexOf(needle) !== -1;
};

const isTransferBundle = (value) =>
  Boolean(value) &&
  value.kind === TRANSFER_KIND &&
  value.version === TRANSFER_VERSION &&
  Array.isArray(value.plans) &&
  value.plans.length > 0;

const TransferDrawer = ({ open, mode, onClose }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);
  const transfer = t("LIST.TRANSFER", { returnObjects: true });
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const [bundle, setBundle] = useState(null);
  const [fileError, setFileError] = useState(false);

  const { data, loading } = useQuery(graphql.queries.INSURANCE_PLAN_CHOICES, {
    skip: !open || mode !== "export",
    variables: { orderBy: [{ name: "ASC" }] },
  });
  const [exportPlans, { loading: exporting }] = useLazyQuery(
    graphql.queries.EXPORT_INSURANCE_PLANS,
    { fetchPolicy: "no-cache" },
  );
  const [importPlans, { loading: importing }] = useMutation(
    graphql.mutations.IMPORT_INSURANCE_PLANS,
    { refetchQueries: ["InsurancePlans"] },
  );

  useEffect(() => {
    if (open) return;
    setSearch("");
    setSelected([]);
    setInitialized(false);
    setBundle(null);
    setFileError(false);
  }, [open]);

  useEffect(() => {
    if (!open || mode !== "export" || initialized) return;
    const plans = get(data, "insurancePlans.data") || [];
    if (!plans.length && loading) return;
    setSelected(plans.map((plan) => plan.id));
    setInitialized(true);
  }, [open, mode, initialized, data, loading]);

  const plans = (get(data, "insurancePlans.data") || []).filter((plan) =>
    matchesSearch(plan, search),
  );
  const allVisibleSelected =
    plans.length > 0 && plans.every((plan) => selected.indexOf(plan.id) !== -1);
  const someVisibleSelected = plans.some(
    (plan) => selected.indexOf(plan.id) !== -1,
  );

  const toggleVisible = (checked) => {
    const visibleIds = plans.map((plan) => plan.id);
    setSelected((current) => {
      if (checked) {
        return current.concat(
          visibleIds.filter((id) => current.indexOf(id) === -1),
        );
      }
      return current.filter((id) => visibleIds.indexOf(id) === -1);
    });
  };

  const togglePlan = (id, checked) => {
    setSelected((current) => {
      if (checked) return current.concat(id);
      return current.filter((item) => item !== id);
    });
  };

  const download = async () => {
    try {
      const result = await exportPlans({ variables: { ids: selected } });
      const exported = get(result, "data.exportInsurancePlans");
      if (!exported) return;
      const blob = new Blob([JSON.stringify(exported, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "insurance-plans.json";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      notification.success({ message: transfer.SUCCESS_EXPORT });
      onClose();
    } catch (error) {
      return error;
    }
  };

  const readFile = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result));
        if (!isTransferBundle(parsed)) {
          setBundle(null);
          setFileError(true);
          return;
        }
        setFileError(false);
        setBundle(parsed);
      } catch (error) {
        setBundle(null);
        setFileError(true);
      }
    };
    reader.readAsText(file);
    return false;
  };

  const confirmImport = async () => {
    try {
      const result = await importPlans({ variables: { data: bundle } });
      const count = get(result, "data.importInsurancePlans.plans", 0);
      notification.success({
        message: t("LIST.TRANSFER.SUCCESS_IMPORT", { count }),
      });
      onClose();
    } catch (error) {
      return error;
    }
  };

  return (
    <Drawer
      title={mode === "export" ? transfer.EXPORT_TITLE : transfer.IMPORT_TITLE}
      width={520}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      {mode === "export" ? (
        <Spin spinning={loading}>
          <Input
            allowClear
            placeholder={transfer.SEARCH}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className="plan-transfer__toolbar">
            <Checkbox
              indeterminate={someVisibleSelected && !allVisibleSelected}
              checked={allVisibleSelected}
              onChange={(event) => toggleVisible(event.target.checked)}
            >
              {transfer.SELECT_ALL}
            </Checkbox>
            <span className="plan-transfer__count">
              {t("LIST.TRANSFER.SELECTED", { count: selected.length })}
            </span>
          </div>
          <div className="plan-transfer__list">
            {plans.length === 0 ? (
              <Empty description={transfer.EMPTY} />
            ) : (
              plans.map((plan) => (
                <div
                  key={plan.id}
                  className="d-flex align--start gap-8 plan-transfer__row"
                >
                  <Checkbox
                    checked={selected.indexOf(plan.id) !== -1}
                    onChange={(event) =>
                      togglePlan(plan.id, event.target.checked)
                    }
                  />
                  <div>
                    <div>{planLabel(plan)}</div>
                    <div className="plan-transfer__meta">{plan.identifier}</div>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="d-flex justify--end gap-8 plan-transfer__actions">
            <Button onClick={onClose}>{transfer.CANCEL}</Button>
            <Button
              type="primary"
              disabled={selected.length === 0}
              loading={exporting}
              onClick={download}
            >
              {transfer.DOWNLOAD}
            </Button>
          </div>
        </Spin>
      ) : (
        <div>
          <Upload
            accept="application/json,.json"
            maxCount={1}
            showUploadList={false}
            beforeUpload={readFile}
          >
            <Button icon={<MdOutlineUploadFile size={18} />}>
              {transfer.FILE}
            </Button>
          </Upload>
          {fileError ? (
            <div className="plan-transfer__error">{transfer.INVALID}</div>
          ) : null}
          {bundle ? (
            <div className="plan-transfer__preview">
              <div className="plan-transfer__preview-title">
                {transfer.PREVIEW}
              </div>
              {bundle.plans.map((plan) => (
                <div key={plan.id} className="plan-transfer__row">
                  <div>{planLabel(plan)}</div>
                  <div className="plan-transfer__meta">{plan.identifier}</div>
                </div>
              ))}
            </div>
          ) : null}
          <div className="d-flex justify--end gap-8 plan-transfer__actions">
            <Button onClick={onClose}>{transfer.CANCEL}</Button>
            <Button
              type="primary"
              disabled={!bundle}
              loading={importing}
              onClick={confirmImport}
            >
              {transfer.CONFIRM}
            </Button>
          </div>
        </div>
      )}
    </Drawer>
  );
};

TransferDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(["export", "import"]).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TransferDrawer;
